import type { Translate } from '@/i18n/translate';
import type { ExternalReleasePurpose, ExternalReleaseVersionType } from '../types';

export const VERSION_TYPE_LABELS: Record<ExternalReleaseVersionType, string> = {
  sop_ota: 'SOP/OTA版本',
  engineering: '工程版本',
  vas: 'VAS',
  poc: 'POC',
  customer_eval: '客户评测',
  mtour: 'Mtour',
  official: '正式版本',
};

/** Translated copy of the version-type labels; `value` stays untouched. */
export const getVersionTypeLabels = (t: Translate): Record<ExternalReleaseVersionType, string> => {
  const entries = Object.entries(VERSION_TYPE_LABELS) as [ExternalReleaseVersionType, string][];
  return Object.fromEntries(entries.map(([key, label]) => [key, t(label)])) as Record<
    ExternalReleaseVersionType,
    string
  >;
};

/** BSP 前端可选项；official 仅用于历史数据与 RPA 展示，不允许重新选中 */
export const getVersionTypeOptions = (t: Translate) => {
  const labels = getVersionTypeLabels(t);
  return [
    { value: 'sop_ota', label: labels.sop_ota },
    { value: 'engineering', label: labels.engineering },
    { value: 'vas', label: labels.vas },
    { value: 'poc', label: labels.poc },
    { value: 'customer_eval', label: labels.customer_eval },
    { value: 'mtour', label: labels.mtour },
  ];
};

export const versionTypeLabel = (value: string | null | undefined, t: Translate): string =>
  (value && t(VERSION_TYPE_LABELS[value as ExternalReleaseVersionType] || '')) || '-';

/** 释放目的：CUSTOMER=给客户释放（默认），TEST=测试上传流程 */
export const RELEASE_PURPOSE_LABELS: Record<ExternalReleasePurpose, string> = {
  CUSTOMER: 'CUSTOMER用于给客户释放',
  TEST: 'TEST用于测试上传流程',
};

/** 下拉备注（不含枚举值本身，避免与 label 重复） */
const RELEASE_PURPOSE_REMARKS: Record<ExternalReleasePurpose, string> = {
  CUSTOMER: '用于给客户释放',
  TEST: '用于测试上传流程',
};

/** 下拉选项：label 只放枚举值，备注走 description，由 optionRender 灰色展示 */
export const getReleasePurposeOptions = (t: Translate) =>
  (Object.keys(RELEASE_PURPOSE_LABELS) as ExternalReleasePurpose[]).map((value) => ({
    value,
    label: value,
    description: t(RELEASE_PURPOSE_REMARKS[value]),
  }));

export const releasePurposeLabel = (value: string | null | undefined, t: Translate): string =>
  (value && t(RELEASE_PURPOSE_LABELS[value as ExternalReleasePurpose] || '')) || '-';

/** TEST 山头的项目默认按测试上传流程处理 */
export const DEFAULT_RELEASE_PURPOSE: ExternalReleasePurpose = 'CUSTOMER';
export const releasePurposeForMountain = (mountainName?: string | null): ExternalReleasePurpose =>
  (mountainName || '').trim().toUpperCase() === 'TEST' ? 'TEST' : DEFAULT_RELEASE_PURPOSE;
